/**
 * Billing Service
 * Handles subscription billing API integration
 *
 * IMPORTANT: Billing endpoints are on the SHARED AUTH API (auth-dev.rocketpricing.com),
 * NOT the stack-specific data API. This ensures billing works regardless of which
 * developer stack is selected in the stack switcher.
 *
 * Backend endpoints (see docs/subscriptions/07_api_contract.md):
 * - POST /billing/checkout-session
 * - POST /billing/portal-session
 * - GET /billing/current-plan
 * - GET /billing/usage
 * - GET /billing/subscription-status
 */

import { config } from '../config/environments'
import type { ApiResponse } from '../types/api.types'
import type {
  CurrentPlan,
  UsageMetrics,
  SubscriptionStatusData,
  CheckoutSessionData,
  PortalSessionData
} from '../types/billing.types'

class BillingService {
  /**
   * Make a GET request to the auth API billing endpoints
   */
  private async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    const url = `${config.authApiBaseUrl}${endpoint}`
    console.log('🌐 GET:', url)

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // Send httpOnly cookies
        headers: { 'Content-Type': 'application/json' }
      })

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          message: data.errors?.[0]?.message || `HTTP ${response.status}`,
          errors: data.errors || [{ code: `HTTP_${response.status}`, message: response.statusText }]
        }
      }

      // Unwrap the wrapped response format
      return {
        success: true,
        data: data.data as T,
        meta: data.meta
      }
    } catch (error) {
      console.error('❌ Billing API Error:', error)
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error',
        errors: [{ code: 'NETWORK_ERROR', message: 'Failed to fetch' }]
      }
    }
  }

  /**
   * Make a POST request to the auth API billing endpoints
   */
  private async post<T>(endpoint: string, body: Record<string, unknown>): Promise<ApiResponse<T>> {
    const url = `${config.authApiBaseUrl}${endpoint}`
    console.log('🌐 POST:', url)

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Send httpOnly cookies
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          message: data.errors?.[0]?.message || `HTTP ${response.status}`,
          errors: data.errors || [{ code: `HTTP_${response.status}`, message: response.statusText }]
        }
      }

      // Unwrap the wrapped response format
      return {
        success: true,
        data: data.data as T,
        meta: data.meta
      }
    } catch (error) {
      console.error('❌ Billing API Error:', error)
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error',
        errors: [{ code: 'NETWORK_ERROR', message: 'Failed to fetch' }]
      }
    }
  }

  /**
   * Create Stripe Checkout session for new subscription or upgrade
   *
   * @param priceId Stripe Price ID (e.g., "price_GrowthMonthly")
   * @param trialPeriodDays Optional trial duration (default: 0)
   * @returns Checkout URL and session ID
   */
  async createCheckoutSession(
    priceId: string,
    trialPeriodDays?: number
  ): Promise<ApiResponse<CheckoutSessionData>> {
    return await this.post<CheckoutSessionData>('/billing/checkout-session', {
      priceId,
      trialPeriodDays
    })
  }

  /**
   * Create Stripe Customer Portal session for self-service billing management
   * (update payment method, change plan, cancel subscription)
   *
   * @returns Customer Portal URL
   */
  async createPortalSession(): Promise<ApiResponse<PortalSessionData>> {
    return await this.post<PortalSessionData>('/billing/portal-session', {})
  }

  /**
   * Get current subscription plan details with limits and status
   *
   * @returns Current plan data including tier, status, limits, next billing date
   */
  async getCurrentPlan(): Promise<ApiResponse<CurrentPlan>> {
    return await this.get<CurrentPlan>('/billing/current-plan')
  }

  /**
   * Get current usage metrics against plan limits
   *
   * @returns SKU count and SmartImport run usage with percentages
   */
  async getUsage(): Promise<ApiResponse<UsageMetrics>> {
    return await this.get<UsageMetrics>('/billing/usage')
  }

  /**
   * Quick check if subscription is active and in good standing
   * (used for access control)
   *
   * @returns Active status and whether payment is required
   */
  async getSubscriptionStatus(): Promise<ApiResponse<SubscriptionStatusData>> {
    return await this.get<SubscriptionStatusData>('/billing/subscription-status')
  }
}

export const billingService = new BillingService()
